import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format, parseISO } from 'date-fns';

const formatDate = (s) => {
  if (!s) return '—';
  try { return format(parseISO(s), 'MMM d, yyyy'); } catch (_) { return s; }
};

const formatMoney = (n) => `$${Number(n || 0).toFixed(2)}`;

const wrapText = (doc, text, maxWidth) =>
  doc.splitTextToSize(String(text || ''), maxWidth);

export function buildInvoicePDF(invoice) {
  if (!invoice) throw new Error('No invoice');

  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 48;
  let y = margin;

  // ===== HEADER =====
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(15, 23, 42);
  doc.text('PROP VEIW', margin, y);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text('Property Management', margin, y + 14);

  // Invoice title (right)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(15, 23, 42);
  doc.text('INVOICE', pageWidth - margin, y, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(71, 85, 105);
  doc.text(`#${invoice.invoice_number || ''}`, pageWidth - margin, y + 16, { align: 'right' });

  y += 44;
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.5);
  doc.line(margin, y, pageWidth - margin, y);
  y += 20;

  // ===== BILL TO + META =====
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text('BILL TO', margin, y);

  doc.setFontSize(9);
  doc.text('INVOICE DATE', pageWidth - margin - 160, y);
  doc.text('DUE DATE', pageWidth - margin, y, { align: 'right' });

  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.text(invoice.client_name || invoice.property_name || '—', margin, y + 15);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);
  if (invoice.client_email) doc.text(invoice.client_email, margin, y + 29);
  doc.text(`Property: ${invoice.property_name || '—'}`, margin, y + 43);

  doc.setFontSize(10);
  doc.setTextColor(15, 23, 42);
  doc.text(formatDate(invoice.created_at), pageWidth - margin - 160, y + 15);
  doc.text(invoice.due_date ? formatDate(invoice.due_date) : '—', pageWidth - margin, y + 15, { align: 'right' });

  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text('PERIOD', pageWidth - margin - 160, y + 33);
  doc.setFontSize(10);
  doc.setTextColor(15, 23, 42);
  doc.text(
    `${formatDate(invoice.date_from)} — ${formatDate(invoice.date_to)}`,
    pageWidth - margin - 160,
    y + 47
  );

  y += 72;

  // ===== ABOVE LINE ITEMS: Manager notes + Work notes =====
  const ensureSpaceEarly = (needed) => {
    if (y + needed > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  const contentW = pageWidth - margin * 2;

  if (invoice.manager_notes) {
    const lines = wrapText(doc, invoice.manager_notes, contentW - 20);
    const blockH = 26 + lines.length * 12 + 8;
    ensureSpaceEarly(blockH);
    doc.setFillColor(15, 23, 42);
    doc.rect(margin, y, 3, blockH - 8, 'F');
    doc.setFillColor(248, 250, 252);
    doc.rect(margin + 3, y, contentW - 3, blockH - 8, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text('MESSAGE', margin + 10, y + 14);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.text(lines, margin + 10, y + 28);
    y += blockH;
  }

  if (invoice.work_notes && invoice.work_notes.length > 0) {
    ensureSpaceEarly(30);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(`WORK NOTES (${invoice.work_notes.length})`, margin, y + 12);
    y += 20;

    const workRows = invoice.work_notes.map((n) => [
      (n.source || 'note').toUpperCase(),
      n.task_title || '—',
      n.hours ? `${Number(n.hours).toFixed(1)}h` : '—',
      n.text || '',
    ]);
    autoTable(doc, {
      startY: y,
      head: [['Type', 'Task', 'Hours', 'Note']],
      body: workRows,
      theme: 'plain',
      styles: { fontSize: 8, cellPadding: 5, textColor: [15, 23, 42], valign: 'top' },
      headStyles: {
        fillColor: [241, 245, 249],
        textColor: [71, 85, 105],
        fontStyle: 'bold',
        fontSize: 8,
      },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 130 },
        2: { halign: 'right', cellWidth: 44 },
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: margin, right: margin },
    });
    y = doc.lastAutoTable.finalY + 12;
  }

  // ===== LINE ITEMS TABLE =====
  const rows = (invoice.line_items || []).map((li) => [
    li.date ? formatDate(li.date) : '—',
    `${(li.task_type || '').toUpperCase()}  ${li.description || ''}`,
    li.hours ? `${Number(li.hours).toFixed(1)}h` : '—',
    li.hourly_rate ? `${formatMoney(li.hourly_rate)}/hr` : '—',
    formatMoney(li.amount),
  ]);

  autoTable(doc, {
    startY: y,
    head: [['Date', 'Description', 'Hours', 'Rate', 'Amount']],
    body: rows,
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: 6, textColor: [15, 23, 42] },
    headStyles: {
      fillColor: [15, 23, 42],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9,
    },
    columnStyles: {
      0: { cellWidth: 78 },
      2: { halign: 'right', cellWidth: 50 },
      3: { halign: 'right', cellWidth: 70 },
      4: { halign: 'right', cellWidth: 74 },
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    margin: { left: margin, right: margin },
  });

  y = doc.lastAutoTable.finalY + 12;

  // ===== TOTALS =====
  const totalHours = (invoice.line_items || []).reduce(
    (s, i) => s + (Number(i.hours) || 0), 0
  );

  const boxX = pageWidth - margin - 220;
  const boxW = 220;

  const drawTotalRow = (label, value, bold = false) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(bold ? 11 : 10);
    doc.setTextColor(bold ? 15 : 71, bold ? 23 : 85, bold ? 42 : 105);
    doc.text(label, boxX, y);
    doc.setTextColor(15, 23, 42);
    doc.text(value, boxX + boxW, y, { align: 'right' });
    y += 16;
  };

  drawTotalRow('Total hours', `${totalHours.toFixed(1)}h`);
  drawTotalRow('Subtotal', formatMoney(invoice.subtotal));
  if (invoice.tax_rate > 0) {
    drawTotalRow(`Tax (${invoice.tax_rate}%)`, formatMoney(invoice.tax_amount));
  }
  y += 4;
  doc.setDrawColor(15, 23, 42);
  doc.setLineWidth(1);
  doc.line(boxX, y - 8, boxX + boxW, y - 8);
  drawTotalRow('TOTAL', formatMoney(invoice.total), true);

  y += 12;

  // ===== MESSAGE / INSTRUCTIONS / NOTES =====
  const ensureSpace = (needed) => {
    if (y + needed > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  const drawBlock = (title, text, opts = {}) => {
    if (!text) return;
    const contentW = pageWidth - margin * 2;
    const lines = wrapText(doc, text, contentW - 20);
    const blockH = 26 + lines.length * 12 + 8;
    ensureSpace(blockH);

    // Background
    doc.setFillColor(opts.fill || 248, opts.fill ? opts.fill : 250, opts.fill ? opts.fill : 252);
    if (opts.accent) {
      doc.setFillColor(15, 23, 42);
      doc.rect(margin, y, 3, blockH - 8, 'F');
      doc.setFillColor(248, 250, 252);
      doc.rect(margin + 3, y, contentW - 3, blockH - 8, 'F');
    } else {
      doc.setFillColor(248, 250, 252);
      doc.rect(margin, y, contentW, blockH - 8, 'F');
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(title.toUpperCase(), margin + 10, y + 14);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.text(lines, margin + 10, y + 28);

    y += blockH;
  };

  if (invoice.custom_message) drawBlock('Message', invoice.custom_message, { accent: true });
  if (invoice.payment_instructions) drawBlock('Payment Instructions', invoice.payment_instructions);
  if (invoice.notes && invoice.include_notes_in_pdf !== false) drawBlock('Notes', invoice.notes);

  // ===== FOOTER =====
  const footerY = pageHeight - 32;
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.5);
  doc.line(margin, footerY, pageWidth - margin, footerY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(148, 163, 184);
  doc.text('Generated by PROP VEIW • propveiw.com', margin, footerY + 14);
  doc.text(
    `Invoice ${invoice.invoice_number || ''}`,
    pageWidth - margin,
    footerY + 14,
    { align: 'right' }
  );

  const safeName = (invoice.invoice_number || 'invoice').replace(/[^a-z0-9-_]/gi, '_');
  return { doc, filename: `${safeName}.pdf` };
}

/**
 * Trigger a download of the invoice PDF (uses the same builder as the preview).
 */
export function generateInvoicePDF(invoice) {
  const { doc, filename } = buildInvoicePDF(invoice);
  doc.save(filename);
}

/**
 * Build the invoice PDF and return a `blob:` URL for embedding in an <iframe>.
 * Callers own revoking the URL via `URL.revokeObjectURL(url)` when unmounted.
 */
export function invoicePdfBlobUrl(invoice) {
  const { doc } = buildInvoicePDF(invoice);
  return URL.createObjectURL(doc.output('blob'));
}
